import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  UploadImageFileInput,
  UploadImageFileOptions,
  UploadImageFileResult,
} from './image-upload-types'
import { uploadFile } from '@repo/studio-utils/utils/upload-file'
import { uploadFileWithTelemetry } from '@repo/studio-utils/analytics/upload-telemetry'
import type { UploadTelemetryTool } from '@repo/studio-utils/analytics/upload-telemetry'

export const IMAGE_PROCESSING_UPLOAD_MUTATION_KEY = ['image-processing', 'upload']

interface InitiateResponse {
  session?: {
    id?: string
  }

  urls?: BatchUrlResponseItem[]
}

interface InitiateVariables {
  urls: BatchUrlRequestItem[]
}

interface BatchUrlRequestItem {
  fileName: string
  fileSize: bigint
}

interface BatchUrlResponseItem {
  uploadId?: string

  url?: string
}

export function useUploadImage({
  initiateMutationFn,
  listQueryKey,
  options,
  onFileUploaded,
  telemetryTool,
}: {
  initiateMutationFn: (variables: InitiateVariables) => Promise<InitiateResponse>
  listQueryKey: string
  options?: UploadImageFileOptions
  onFileUploaded?: (file: File, sessionId: string) => void
  telemetryTool?: UploadTelemetryTool
}) {
  const queryClient = useQueryClient()
  const abortControllersRef = React.useRef<AbortController[]>([])

  function cancelUpload() {
    abortControllersRef.current.forEach(controller => controller.abort())
    abortControllersRef.current = []
  }

  const uploadMutation = useMutation<UploadImageFileResult, Error, UploadImageFileInput>({
    mutationKey: IMAGE_PROCESSING_UPLOAD_MUTATION_KEY,
    mutationFn: async ({ files }: UploadImageFileInput): Promise<UploadImageFileResult> => {
      abortControllersRef.current = []

      const data = await initiateMutationFn({
        urls: files.map(file => ({
          fileName: file.name,
          fileSize: BigInt(file.size),
        })),
      })

      const sessionId = data?.session?.id
      if (!sessionId) {
        throw new Error('Failed to create session')
      }

      options?.onSessionCreated?.(sessionId)

      queryClient.invalidateQueries({ queryKey: [listQueryKey] })

      const uploads = await Promise.all(
        data.urls?.map(async ({ url, uploadId: responseUploadId }, index) => {
          if (!url) {
            throw new Error('Failed to get upload URL')
          }

          const abortController = new AbortController()
          abortControllersRef.current.push(abortController)

          try {
            const file = files[index]
            const uploadOptions = {
              url,
              file,
              signal: abortController.signal,
              onProgress: options?.onProgress,
            }
            await (telemetryTool
              ? uploadFileWithTelemetry({ ...uploadOptions, telemetryTool, sessionId })
              : uploadFile(uploadOptions))
            onFileUploaded?.(file, sessionId ?? '')

            return {
              url,
              fileName: file.name,
              fileSize: String(file.size),
              uploadId: responseUploadId || '',
            }
          } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
              throw new DOMException('Upload Aborted', 'AbortError')
            }
            throw error
          }
        }) || [],
      )

      abortControllersRef.current = []

      return {
        id: sessionId,
        success: true,
        uploads,
      }
    },
    ...options,
    onSettled(...args) {
      options?.onSettled?.(...args)
      queryClient.invalidateQueries({ queryKey: [listQueryKey] })
    },
  })
  return {
    uploadMutation,
    cancelUpload,
  }
}
